import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, Wallet, Users, KanbanSquare, BarChart3, Zap, Check, FolderOpen, Lock, Download, Plane, Car, Route, MapPin, Gauge, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { AuroraSwirl } from "@/components/AuroraSwirl";

const FEATURES = [
  { icon: BarChart3, title: "Unified Dashboard", desc: "Revenue, cash flow forecast, and AI insights in one control room." },
  { icon: Users, title: "Clients & Opportunities", desc: "Track clients and opportunities. Let AI summarize and draft your outreach." },
  { icon: Wallet, title: "Finance & Invoicing", desc: "Send invoices, scan receipts with AI OCR, forecast cash flow." },
  { icon: KanbanSquare, title: "Operations", desc: "Kanban tasks and inventory that keep the business moving." },
  { icon: FolderOpen, title: "Client Portal", desc: "Deliver final photos, videos and files through a secure link — no client login needed. (Business)" },
  { icon: Plane, title: "Travel & Mileage", desc: "Plan trips, auto-calculate mileage, bill drives to clients — plus AI trip planning on Command." },
  { icon: Sparkles, title: "AI Hub", desc: "A co-pilot that knows your business and takes action." },
  { icon: Zap, title: "Content Studio", desc: "Generate posts, emails, and ad copy in seconds." },
];

const PERSONAS = [
  { img: "https://images.unsplash.com/photo-1603574670812-d24560880210?auto=format&fit=crop&w=700&h=525&q=80", title: "Photographers & Videographers", desc: "Book shoots, invoice clients, and never lose an inquiry." },
  { img: "https://images.unsplash.com/photo-1572044162444-ad60f128bdea?auto=format&fit=crop&w=700&h=525&q=80", title: "Designers", desc: "Manage projects and proposals while AI drafts the copy." },
  { img: "https://images.unsplash.com/photo-1561593367-66c79c2294e6?auto=format&fit=crop&w=700&h=525&q=80", title: "Event planners", desc: "Keep every client, deadline and payment in one calendar." },
  { img: "https://images.unsplash.com/photo-1545184180-25d471fe75eb?auto=format&fit=crop&w=700&h=525&q=80", title: "Consultants & freelancers", desc: "Run your whole practice — from first chat to final payout." },
];

const HERO_CHIPS = [
  { icon: Wallet, label: "Invoicing" },
  { icon: Users, label: "Clients" },
  { icon: KanbanSquare, label: "Tasks" },
  { icon: Sparkles, label: "AI co-pilot" },
];

const TIERS = [
  { name: "Free", price: "$0", perks: ["Core modules", "Trip planner + mileage tracker", "20 AI credits / mo", "Single user"] },
  { name: "BizForge Core", price: "$15", perks: ["Everything in Free", "500 AI credits / mo", "Up to 2 users", "Full analytics"], highlight: true },
  { name: "BizForge Command", price: "$30", perks: ["Everything in BizForge Core", "AI Travel Assistant (packing, budget & more)", "5000 AI credits / mo", "Up to 5 users", "Client portal for file deliverables"] },
];

const HOW_STEPS = [
  { n: "1", title: "Set up in minutes", desc: "Tell us your business name, industry and goal — your workspace is tailored instantly, with sample data to explore." },
  { n: "2", title: "Bring your work together", desc: "Add contacts, deals, invoices, tasks and events in one place. No more juggling five different apps." },
  { n: "3", title: "Let AI do the busywork", desc: "Get daily insights, draft outreach emails, scan receipts, and see exactly what needs your attention today." },
];

const TRUST = [
  { big: "6+", small: "Tools replaced" },
  { big: "All-in-one", small: "Clients · Invoicing · Ops" },
  { big: "AI", small: "Built into every module" },
  { big: "Free", small: "To get started" },
];

function DashboardSlide() {
  const bars = [45, 70, 55, 82, 64, 96];
  const stats = [
    { label: "Revenue", value: "$10.7k", cls: "text-accent" },
    { label: "Outstanding", value: "$4.5k", cls: "text-amber-500" },
    { label: "Net", value: "$9.8k", cls: "text-primary" },
  ];
  const pipeline = [
    { label: "New Prospect", dot: "bg-slate-400", val: "$2.5k" },
    { label: "Proposal", dot: "bg-amber-500", val: "$8.0k" },
    { label: "Won", dot: "bg-accent", val: "$2.8k" },
  ];
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="rounded-xl border border-border p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{s.label}</p>
            <p className={`mt-1 font-display text-lg font-bold ${s.cls}`}>{s.value}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-5 gap-3">
        <div className="col-span-3 rounded-xl border border-border p-3">
          <p className="mb-2 text-[11px] font-semibold text-muted-foreground">Revenue vs Expenses</p>
          <div className="flex h-24 items-end gap-2">
            {bars.map((h, i) => (
              <div key={i} className="flex h-full flex-1 items-end justify-center gap-0.5">
                <div className="w-1.5 rounded-t bg-accent" style={{ height: `${h}%` }} />
                <div className="w-1.5 rounded-t bg-amber-400/70" style={{ height: `${h * 0.45}%` }} />
              </div>
            ))}
          </div>
        </div>
        <div className="col-span-2 space-y-2 rounded-xl border border-border p-3">
          <p className="text-[11px] font-semibold text-muted-foreground">Opportunities</p>
          {pipeline.map((p) => (
            <div key={p.label} className="flex items-center justify-between text-[11px]">
              <span className="flex items-center gap-1.5 text-muted-foreground"><span className={`h-1.5 w-1.5 rounded-full ${p.dot}`} /> {p.label}</span>
              <span className="font-semibold">{p.val}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2 rounded-xl bg-accent/10 p-3 text-[11px] font-medium text-accent">
        <Sparkles className="h-3.5 w-3.5 shrink-0" /> AI: Follow up on Acme's Q3 renewal — projected +$13.5k this quarter.
      </div>
    </div>
  );
}

function AIHubSlide() {
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <div className="max-w-[75%] rounded-2xl rounded-br-sm bg-primary px-3.5 py-2 text-[11px] text-primary-foreground">
          How is my cash flow this month, and who owes me money?
        </div>
      </div>
      <div className="flex items-start gap-2">
        <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-accent/15 text-accent"><Sparkles className="h-3.5 w-3.5" /></div>
        <div className="max-w-[85%] space-y-1.5 rounded-2xl rounded-bl-sm border border-border bg-secondary/50 px-3.5 py-2.5 text-[11px] leading-relaxed text-muted-foreground">
          <p className="font-semibold text-foreground">Cash Flow Overview:</p>
          <p>You've collected $10,700 this month against $890 in expenses — a healthy net of $9,810.</p>
          <p className="font-semibold text-foreground">Outstanding Invoices:</p>
          <p>Acme Corp owes $4,500 on INV-1003, due July 12. It's your largest open balance — worth a nudge today.</p>
        </div>
      </div>
      <div className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-[11px] text-muted-foreground">
        <span className="flex-1">Ask anything about your business…</span>
        <span className="rounded-md bg-accent px-2 py-1 text-[10px] font-semibold text-accent-foreground">Send</span>
      </div>
    </div>
  );
}

function InvoicingSlide() {
  const rows = [
    { n: "INV-1004", who: "Acme Corp", amt: "$4,500", st: "Sent", cls: "text-amber-500 bg-amber-500/10" },
    { n: "INV-1003", who: "Northwind Studios", amt: "$2,800", st: "Paid", cls: "text-accent bg-accent/10" },
    { n: "INV-1002", who: "Sarah Mitchell", amt: "$1,200", st: "Paid", cls: "text-accent bg-accent/10" },
  ];
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="rounded-xl border border-border p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Collected</p>
          <p className="mt-1 font-display text-lg font-bold text-accent">$10.7k</p>
        </div>
        <div className="rounded-xl border border-border p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Outstanding</p>
          <p className="mt-1 font-display text-lg font-bold text-amber-500">$4.5k</p>
        </div>
        <span className="flex items-center gap-1.5 rounded-lg bg-accent px-2.5 py-1.5 text-[10px] font-semibold text-accent-foreground"><Wallet className="h-3 w-3" /> New invoice</span>
      </div>
      <div className="overflow-hidden rounded-xl border border-border">
        {rows.map((r, i) => (
          <div key={r.n} className={`flex items-center justify-between px-3 py-2.5 text-[11px] ${i > 0 ? "border-t border-border" : ""}`}>
            <div><span className="font-semibold">{r.n}</span> <span className="text-muted-foreground">· {r.who}</span></div>
            <div className="flex items-center gap-2">
              <span className="font-semibold">{r.amt}</span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${r.cls}`}>{r.st}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PortalSlide() {
  const files = [
    { icon: FolderOpen, name: "Smith_Wedding_Finals.zip", size: "248 MB" },
    { icon: Download, name: "Hero_Shot_edited.jpg", size: "18 MB" },
    { icon: Download, name: "Highlight_Reel.mp4", size: "196 MB" },
  ];
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between rounded-xl border border-dashed border-accent/40 bg-accent/5 p-3">
        <div className="flex items-center gap-2 text-[11px]">
          <Lock className="h-3.5 w-3.5 text-accent" />
          <span className="text-muted-foreground">bizforge.services/portal/<span className="text-foreground">a7f3…</span></span>
        </div>
        <span className="rounded-md bg-accent px-2 py-1 text-[10px] font-semibold text-accent-foreground">Copy link</span>
      </div>
      <p className="text-[11px] font-semibold text-muted-foreground">Smith Wedding — Final Delivery</p>
      <div className="space-y-2">
        {files.map((f) => (
          <div key={f.name} className="flex items-center gap-3 rounded-xl border border-border p-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary"><f.icon className="h-4 w-4 text-muted-foreground" /></div>
            <div className="min-w-0 flex-1"><p className="truncate text-[11px] font-medium">{f.name}</p><p className="text-[10px] text-muted-foreground">{f.size}</p></div>
            <Download className="h-3.5 w-3.5 text-accent" />
          </div>
        ))}
      </div>
    </div>
  );
}

function TripPlannerSlide() {
  const trips = [
    { dest: "Austin, TX", purpose: "Wedding shoot", dates: "Aug 10 → 14", st: "Upcoming", cls: "text-primary bg-primary/10" },
    { dest: "Lake Como, IT", purpose: "Destination shoot", dates: "Sep 2 → 9", st: "Active", cls: "text-accent bg-accent/10" },
  ];
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        {trips.map((t) => (
          <div key={t.dest} className="rounded-xl border border-border p-3">
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${t.cls}`}>{t.st}</span>
            <p className="mt-2 flex items-center gap-1 text-[12px] font-semibold"><MapPin className="h-3 w-3 text-accent" /> {t.dest}</p>
            <p className="text-[10px] text-muted-foreground">{t.purpose}</p>
            <p className="mt-1 text-[10px] text-muted-foreground">{t.dates}</p>
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-border p-3">
        <p className="mb-2 text-[11px] font-semibold text-muted-foreground">Itinerary · Austin, TX</p>
        <div className="space-y-1.5 text-[11px]">
          {[["09:00", "Venue walkthrough"], ["13:00", "Golden-hour scout"], ["18:30", "Ceremony coverage"]].map(([time, d]) => (
            <div key={time} className="flex items-center gap-2.5">
              <span className="w-9 shrink-0 font-medium text-accent">{time}</span>
              <span className="text-muted-foreground">{d}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2 rounded-xl bg-accent/10 p-3 text-[11px] font-medium text-accent">
        <Sparkles className="h-3.5 w-3.5 shrink-0" /> AI Packing List, Budget & Timezone tips — ready on Command.
      </div>
    </div>
  );
}

function MileageSlide() {
  const rows = [
    { route: "Studio → Venue", cat: "Scouting", miles: "42.0", total: "$29.40" },
    { route: "Austin → Dallas", cat: "Client visit", miles: "195.2", total: "$136.64" },
    { route: "Office → Airport", cat: "Airport", miles: "18.6", total: "$13.02" },
  ];
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-border p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Total miles</p>
          <p className="mt-1 font-display text-lg font-bold">255.8</p>
        </div>
        <div className="rounded-xl border border-border p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Reimbursement</p>
          <p className="mt-1 font-display text-lg font-bold text-accent">$179.06</p>
        </div>
      </div>
      <div className="overflow-hidden rounded-xl border border-border">
        {rows.map((r, i) => (
          <div key={r.route} className={`flex items-center justify-between px-3 py-2.5 text-[11px] ${i > 0 ? "border-t border-border" : ""}`}>
            <div className="min-w-0"><p className="font-medium">{r.route}</p><p className="text-[10px] text-muted-foreground">{r.cat} · {r.miles} mi</p></div>
            <div className="flex items-center gap-2">
              <span className="font-semibold">{r.total}</span>
              <span className="rounded-md bg-accent px-2 py-0.5 text-[10px] font-semibold text-accent-foreground">Bill</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 rounded-xl bg-accent/10 p-3 text-[11px] font-medium text-accent">
        <Route className="h-3.5 w-3.5 shrink-0" /> Real driving distance auto-calculated — one tap to bill a client.
      </div>
    </div>
  );
}

const HERO_SLIDES = [
  { label: "Dashboard", render: DashboardSlide },
  { label: "AI Hub", render: AIHubSlide },
  { label: "Invoicing & Expenses", render: InvoicingSlide },
  { label: "Trip Planner", render: TripPlannerSlide },
  { label: "Mileage Tracker", render: MileageSlide },
  { label: "Client Portal", render: PortalSlide },
];

function HeroPreview() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setI((p) => (p + 1) % HERO_SLIDES.length), 3800);
    return () => clearInterval(t);
  }, [paused]);
  const Slide = HERO_SLIDES[i].render;
  return (
    <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid="hero-slideshow">
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
        <div className="flex items-center gap-2 border-b border-border bg-secondary/50 px-4 py-3">
          <span className="h-3 w-3 rounded-full bg-rose-400" />
          <span className="h-3 w-3 rounded-full bg-amber-400" />
          <span className="h-3 w-3 rounded-full bg-emerald-400" />
          <span className="ml-3 text-xs font-medium text-muted-foreground">BizForge AI · {HERO_SLIDES[i].label}</span>
        </div>
        <div key={i} className="min-h-[268px] animate-fade-up p-4 sm:p-5">
          <Slide />
        </div>
      </div>
      <div className="mt-4 flex justify-center gap-2" data-testid="hero-slideshow-dots">
        {HERO_SLIDES.map((s, idx) => (
          <button
            key={s.label}
            onClick={() => setI(idx)}
            aria-label={`Show ${s.label}`}
            data-testid={`hero-dot-${idx}`}
            className={`h-2 rounded-full transition-all ${idx === i ? "w-6 bg-accent" : "w-2 bg-border hover:bg-muted-foreground/40"}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      <header className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary"><Zap className="h-5 w-5 text-primary-foreground" strokeWidth={2.5} /></div>
          <span className="font-display text-lg font-bold tracking-tight">BizForge<span className="text-accent">AI</span></span>
        </div>
        <div className="flex items-center gap-2">
          {user ? (
            <Button onClick={() => navigate("/dashboard")} data-testid="landing-dashboard-button">Go to Dashboard</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate("/login")} data-testid="landing-login-button">Log in</Button>
              <Button onClick={() => navigate("/register")} data-testid="landing-signup-button">Get started</Button>
            </>
          )}
        </div>
      </header>

      <section className="relative mx-auto max-w-7xl px-6 pt-12 pb-20 lg:pt-20">
        <AuroraSwirl />
        <div className="relative grid items-center gap-12 lg:grid-cols-2">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-semibold text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5 text-accent" /> For freelancers, solopreneurs & small teams
            </span>
            <h1 className="mt-5 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Run your whole business from <span className="text-accent">one intelligent</span> workspace.
            </h1>
            <p className="mt-5 max-w-lg text-lg text-muted-foreground">
              Whether you're a photographer, designer, planner or consultant — BizForge AI brings your clients, invoices, and tasks into one place, with an AI co-pilot that handles the busywork for you.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" onClick={() => navigate("/register")} data-testid="hero-cta-button" className="group">
                Start free <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/login")}>See it in action</Button>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {HERO_CHIPS.map((c) => (
                <span key={c.label} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground">
                  <c.icon className="h-3.5 w-3.5 text-accent" /> {c.label}
                </span>
              ))}
            </div>
          </div>
          <HeroPreview />
        </div>
      </section>

      <section className="border-y border-border bg-secondary/30">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-6 py-8 text-center sm:grid-cols-4">
          {TRUST.map((t) => (
            <div key={t.small}>
              <p className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{t.big}</p>
              <p className="mt-1 text-xs text-muted-foreground sm:text-sm">{t.small}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16">
        <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Built for how you work.</h2>
        <p className="mt-2 max-w-xl text-muted-foreground">From first inquiry to final invoice, BizForge AI fits the way freelancers, solopreneurs and small teams actually run.</p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {PERSONAS.map((p, i) => (
            <div key={p.title} className="group overflow-hidden rounded-2xl border border-border bg-card animate-fade-up" style={{ animationDelay: `${i * 70}ms` }}>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={p.img} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-4">
                <h3 className="font-display text-base font-semibold">{p.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16">
        <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Up and running in three steps.</h2>
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {HOW_STEPS.map((s, i) => (
            <div key={s.n} className="relative rounded-2xl border border-border bg-card p-6 animate-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary font-display text-lg font-bold text-primary-foreground">{s.n}</div>
              <h3 className="mt-4 font-display text-lg font-semibold">{s.title}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16">
        <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Everything, unified.</h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <div key={f.title} className="rounded-2xl border border-border bg-card p-6 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent"><f.icon className="h-5 w-5" /></div>
              <h3 className="mt-4 font-display text-lg font-semibold">{f.title}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16">
        <div className="grid items-center gap-10 rounded-3xl border border-border bg-card p-8 lg:grid-cols-2 lg:p-12">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/10 px-3 py-1 text-xs font-semibold text-accent" data-testid="portal-spotlight-badge"><FolderOpen className="h-3.5 w-3.5" /> BizForge Command</span>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl">A polished client portal for your deliverables.</h2>
            <p className="mt-3 text-muted-foreground">Stop wrestling with WeTransfer links and cluttered Dropbox folders. Upload final photos, videos, and documents into a branded gallery and hand your client one secure link — they download everything without ever creating an account.</p>
            <ul className="mt-6 space-y-3">
              <li className="flex items-start gap-3 text-sm"><Download className="mt-0.5 h-4 w-4 shrink-0 text-accent" /> <span>Share galleries up to <span className="font-semibold text-foreground">250&nbsp;MB per file</span> — perfect for high-res shoots and video.</span></li>
              <li className="flex items-start gap-3 text-sm"><Lock className="mt-0.5 h-4 w-4 shrink-0 text-accent" /> <span>Protect each link with an <span className="font-semibold text-foreground">optional access code</span> and expiry date.</span></li>
              <li className="flex items-start gap-3 text-sm"><Users className="mt-0.5 h-4 w-4 shrink-0 text-accent" /> <span>No client logins, no friction — just a clean, professional download page.</span></li>
            </ul>
            <Button className="mt-7" onClick={() => navigate("/register")} data-testid="portal-spotlight-cta">Start delivering with BizForge Command <ArrowRight className="ml-2 h-4 w-4" /></Button>
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-border">
            <img src="https://images.pexels.com/photos/15717262/pexels-photo-15717262.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Client portal gallery of delivered photos" className="h-full w-full object-cover" data-testid="portal-spotlight-image" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16">
        <div className="grid items-center gap-10 rounded-3xl border border-border bg-card p-8 lg:grid-cols-2 lg:p-12">
          <div className="relative order-last overflow-hidden rounded-2xl border border-border lg:order-first">
            <img src="https://images.unsplash.com/photo-1618445902270-40c36803f5b0?crop=entropy&cs=srgb&fm=jpg&q=85&w=940&h=650&fit=crop" alt="Freelancer planning a trip with a laptop on the road" className="h-full w-full object-cover" data-testid="travel-spotlight-image" />
          </div>
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/10 px-3 py-1 text-xs font-semibold text-accent" data-testid="travel-spotlight-badge"><Plane className="h-3.5 w-3.5" /> BizForge Command</span>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl">AI travel planning for freelancers on the move.</h2>
            <p className="mt-3 text-muted-foreground">Every shoot, wedding, or client trip comes with logistics. Plan trips and track mileage for free — then let BizForge Command turn your trip details into instant, practical plans so you show up prepared, on budget and on time.</p>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              <li className="flex items-start gap-3 text-sm"><Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-accent" /> <span><span className="font-semibold text-foreground">Trip Summary</span> — a clean briefing of purpose, dates and prep.</span></li>
              <li className="flex items-start gap-3 text-sm"><FolderOpen className="mt-0.5 h-4 w-4 shrink-0 text-accent" /> <span><span className="font-semibold text-foreground">Packing List</span> — tailored to the shoot, weather and itinerary.</span></li>
              <li className="flex items-start gap-3 text-sm"><Gauge className="mt-0.5 h-4 w-4 shrink-0 text-accent" /> <span><span className="font-semibold text-foreground">Budget Estimate</span> — travel, lodging, meals and per-trip costs.</span></li>
              <li className="flex items-start gap-3 text-sm"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-accent" /> <span><span className="font-semibold text-foreground">Timezone Tips</span> — jet-lag help and the best times to schedule.</span></li>
              <li className="flex items-start gap-3 text-sm sm:col-span-2"><Route className="mt-0.5 h-4 w-4 shrink-0 text-accent" /> <span><span className="font-semibold text-foreground">Optimize My Trip</span> — smarter sequencing and cost savings, drawn from your real trip data.</span></li>
            </ul>
            <Button className="mt-7" onClick={() => navigate("/register")} data-testid="travel-spotlight-cta">Unlock AI Travel with BizForge Command <ArrowRight className="ml-2 h-4 w-4" /></Button>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16">
        <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Simple, honest pricing.</h2>
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {TIERS.map((t) => (
            <div key={t.name} className={`rounded-2xl border p-6 ${t.highlight ? "border-accent bg-accent/5 shadow-md" : "border-border bg-card"}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg font-semibold">{t.name}</h3>
                {t.highlight && <span className="rounded-full bg-accent px-2.5 py-0.5 text-xs font-semibold text-accent-foreground">Popular</span>}
              </div>
              <p className="mt-3"><span className="font-display text-3xl font-bold">{t.price}</span><span className="text-muted-foreground">/mo</span></p>
              <ul className="mt-5 space-y-2.5">
                {t.perks.map((p) => (
                  <li key={p} className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-accent" /> {p}</li>
                ))}
              </ul>
              <Button className="mt-6 w-full" variant={t.highlight ? "default" : "outline"} onClick={() => navigate("/register")}>Get {t.name}</Button>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-border py-8 text-center text-sm text-muted-foreground">
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
          <Link to="/terms" className="hover:text-foreground transition-colors" data-testid="footer-terms-link">Terms of Service</Link>
          <Link to="/privacy" className="hover:text-foreground transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
        </div>
        <p className="mt-4">© 2026 BizForge AI. Built for founders who move fast.</p>
      </footer>
    </div>
  );
}
